
import React from 'react';
import { Link } from 'react-router-dom';

const SupportModels: React.FC = () => {
  return (
    <>
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue" id="support-models">Common IT Support Models Explained</h2>
      <p className="mb-4 text-gray-700 leading-relaxed">
        There are several approaches to IT support, each with distinct characteristics that make them suitable for different business stages:
      </p>

      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">1. Break/Fix Support</h3>
      <p className="mb-3 text-gray-700 leading-relaxed">
        This traditional approach involves calling technical support only when something breaks, with payment made per incident or hourly. While seemingly cost-effective initially, this reactive model often leads to unpredictable expenses and extended downtime, as explored in our guide on <Link to="/blog/cost-of-it-downtime-prevention" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue underline">the true cost of IT downtime</Link>.
      </p>
      <div className="mb-6 pl-5 border-l-4 border-itmgmt-light-blue">
        <p className="italic text-gray-700">
          <strong>Best suited for:</strong> Very small businesses with minimal technology dependencies, limited budgets, and infrequent IT issues.
        </p>
        <p className="italic text-gray-700 mt-2">
          <strong>Limitations:</strong> Reactive approach leads to unpredictable costs, downtime, and lack of strategic planning.
        </p>
      </div>

      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">2. In-House IT Staff</h3>
      <p className="mb-3 text-gray-700 leading-relaxed">
        Hiring one or more dedicated IT employees who handle all aspects of your technology management internally. This approach provides direct control but requires significant investment in staffing and training, including keeping your team updated on evolving <Link to="/blog/cybersecurity-best-practices-small-business" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue underline">cybersecurity best practices</Link>.
      </p>
      <div className="mb-6 pl-5 border-l-4 border-itmgmt-light-blue">
        <p className="italic text-gray-700">
          <strong>Best suited for:</strong> Mid-sized to larger organizations with consistent IT demands, specialized systems, and sufficient budget for full-time professionals.
        </p>
        <p className="italic text-gray-700 mt-2">
          <strong>Limitations:</strong> High fixed costs, knowledge limitations of individuals, coverage gaps during vacations/illness, and difficulty scaling quickly.
        </p>
      </div>

      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">3. Managed IT Services</h3>
      <p className="mb-3 text-gray-700 leading-relaxed">
        Outsourcing some or all IT functions to a specialized provider for a fixed monthly fee, with proactive monitoring and management. Our <Link to="/services/managed-it-services" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue underline">managed IT services</Link> deliver enterprise-grade technology support with predictable costs and without the overhead of a large in-house team.
      </p>
      <div className="mb-6 pl-5 border-l-4 border-itmgmt-light-blue">
        <p className="italic text-gray-700">
          <strong>Best suited for:</strong> Growing businesses seeking predictable IT costs, proactive support, and access to broader expertise without the overhead of a large in-house team.
        </p>
        <p className="italic text-gray-700 mt-2">
          <strong>Limitations:</strong> Less direct control, potential response time concerns if not carefully vetted, and varying quality between providers.
        </p>
      </div>

      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue">4. Hybrid Approach</h3>
      <p className="mb-3 text-gray-700 leading-relaxed">
        Combining in-house IT staff with managed services or specialized external support for specific functions or technologies. This approach can be particularly effective for businesses with unique <Link to="/services/specialty-services" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue underline">specialty IT requirements</Link> that complement their internal capabilities.
      </p>
      <div className="mb-6 pl-5 border-l-4 border-itmgmt-light-blue">
        <p className="italic text-gray-700">
          <strong>Best suited for:</strong> Established businesses with some specialized in-house expertise but seeking additional capabilities or coverage in specific areas.
        </p>
        <p className="italic text-gray-700 mt-2">
          <strong>Limitations:</strong> Requires clear role definition and strong communication to avoid gaps or overlaps in responsibility.
        </p>
      </div>

      <div className="bg-blue-50 p-6 rounded-lg mb-8">
        <h3 className="text-xl font-semibold mb-3 text-itmgmt-dark-blue">The Cost Factor: Beyond Dollars</h3>
        <p className="text-gray-700 mb-3">
          When evaluating IT support models, consider both direct costs and hidden expenses:
        </p>
        <ul className="list-disc ml-6 mb-3 text-gray-700">
          <li className="mb-2"><strong>Downtime costs:</strong> Average small business downtime costs $8,000+ per hour (see our detailed analysis of <Link to="/blog/cost-of-it-downtime-prevention" className="text-itmgmt-medium-blue hover:text-itmgmt-bright-blue underline">IT downtime costs</Link>)</li>
          <li className="mb-2"><strong>Opportunity costs:</strong> Strategic initiatives delayed by IT problems</li>
          <li className="mb-2"><strong>Security breach impacts:</strong> Average cost of $200,000 for small businesses</li>
          <li className="mb-2"><strong>Employee productivity:</strong> Frustration and inefficiency with poorly supported technology</li>
        </ul>
      </div>
    </>
  );
};

export default SupportModels;
